import React, { createContext } from 'react';
import css from './sections.css';
import { ProgressBarLevels, ScoreBadge, Tooltip } from '../../../components';
import { IconCheckmarkL, IconCrossM } from '../../../icons';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';

const SectionContext = createContext(null);

const SectionBlock = ({ children, scoreData }) => {
    return (
        <SectionContext.Provider value={{ scoreData }}>
            <div className={css.root}>{children}</div>
        </SectionContext.Provider>
    );
};

const Header = ({ children }) => <h3 className={css.header}>{children}</h3>;

const Description = ({ children }) => <p className={css.description}>{children}</p>;

const Score = ({ sectionScoreData }) => {
    if (!sectionScoreData) return null;

    return <ScoreBadge scoreData={sectionScoreData} rootClassName={css.scoreBadge} />;
};
/**
 * @param {Object} props
 * @param {object} props.avatarUrl
 * @param {string} [props.rootClassName]
 */
const ScoreLineAvatar = ({ avatarUrl, rootClassName }) => {
    const { url, isAnonymousListing, entityName = 'U' } = avatarUrl;
    return (
        <div className={classNames(css.listingAvatarHolder, rootClassName)}>
            {url ? (
                <div
                    style={{
                        backgroundImage: `url('${url}')`,
                    }}
                    className={classNames(css.listingAvatar, {
                        [css.anonym]: Boolean(isAnonymousListing),
                    })}
                />
            ) : (
                <div className={classNames(css.listingAvatar, css.noAvatar)}>{entityName[0]}</div>
            )}
        </div>
    );
};
const ScoreLineDelimiter = () => <div className={css.delimiter} />;

/**
 * @param {Object} props
 * @param {boolean} [props.matches]
 * @param {boolean} [props.joinedAvatars]
 * @param {string} [props.lineLabel]
 * @param {React.ReactElement} [props.avatars]
 * @param {React.ReactElement} [props.children]
 */
const ScoreLine = ({ matches, lineLabel, avatars, children, joinedAvatars: joinedAvatarsProp }) => (
    <div className={css.infoLineContainer}>
        <p
            className={classNames(css.listingsMatchingInfoHolder, {
                [css.matches]: matches,
            })}
        >
            {matches ? <IconCheckmarkL /> : <IconCrossM />}
            <span>{lineLabel}</span>
            {children}
        </p>
        {avatars && (
            <aside
                className={classNames(css.listingsAvatarHolder, {
                    [css.joinedAvatars]: avatars?.length === 2 || joinedAvatarsProp,
                })}
            >
                {avatars}
            </aside>
        )}
    </div>
);
/**
 * @param {Object} props
 * @param {boolean} [props.matches]
 * @param {string} [props.lineLabel]
 * @param {number} [props.level]
 * @param {number} [props.total]
 * @param {React.ReactElement} [props.images]
 */
const ScoreLineWithLevel = ({ matches, lineLabel, images, level, total }) => {
    const imagesEntries = Object.entries(images);
    const [firstEntryLevelAsKey, imagesArray] = imagesEntries[0];
    const joinedAvatars = imagesArray.length > 1;
    /**
     * .joined-1
     * .joined-2
     * .joined-3
     * .joined-4
     */
    const joinedAvatarsShiftClassName = joinedAvatars
        ? ['joined', firstEntryLevelAsKey].join('-')
        : null;

    return (
        <div className={css.infoLineContainer}>
            <p
                className={classNames(
                    css.listingsMatchingInfoHolder,
                    css.listingsMatchingInfoHolderLevel,
                    {
                        [css.matches]: matches,
                    }
                )}
            >
                {matches ? <IconCheckmarkL /> : <IconCrossM />}
                <span>{lineLabel}</span>
            </p>
            <Tooltip
                tooltipClassName={css.levelTooltip}
                content={
                    <div>
                        <FormattedMessage id={`ProfileSettingsForm.disciplines-level-${level}`} />
                    </div>
                }
                position="top"
            >
                <div className={css.levelsHolder}>
                    <ProgressBarLevels
                        level={level}
                        editAvailable={false}
                        selectedFlag="isFilled"
                        total={total}
                    />
                    <aside
                        className={classNames(css.listingsAvatarHolder, {
                            [css.joinedAvatars]: joinedAvatars,
                            [css[joinedAvatarsShiftClassName]]: joinedAvatars,
                        })}
                    >
                        {imagesEntries.map(([levelAsKey, avatars]) => {
                            const shiftClassName = ['separate', levelAsKey].join('-');

                            return avatars.map((avatarUrl, index) => (
                                <ScoreLineAvatar
                                    key={`ScoreLineAvatar-${index}`}
                                    avatarUrl={avatarUrl}
                                    rootClassName={classNames({
                                        [css[shiftClassName]]: !joinedAvatars,
                                    })}
                                />
                            ));
                        })}
                    </aside>
                </div>
            </Tooltip>
        </div>
    );
};

SectionBlock.Header = Header;
SectionBlock.Description = Description;
SectionBlock.ScoreBadge = Score;
SectionBlock.ScoreLine = ScoreLine;
SectionBlock.ScoreLineWithLevel = ScoreLineWithLevel;
SectionBlock.ScoreLineAvatar = ScoreLineAvatar;
SectionBlock.Delimiter = ScoreLineDelimiter;

export default SectionBlock;
